/**
 * <infinite-scroll data-next-url="..."> auto-loads the next page
 * of products as the sentinel approaches the viewport, then appends
 * the new <li> grid items to #product-grid and updates the URL
 * pointer (or removes itself when there are no more pages).
 *
 * Lives inside #ProductGridContainer so facets.js's grid replacement
 * on filter change re-instantiates this element via connectedCallback —
 * no manual reset needed.
 *
 * Trigger strategy: IntersectionObserver as the primary signal, plus
 * a passive scroll listener and a small periodic check as belt-and-
 * suspenders. IO can drop callbacks during fast flings, throttle on
 * background tabs, and skip the initial-state callback in some
 * Shopify re-render flows; the scroll listener catches those cases.
 *
 * Recovery: exponential-backoff retry on fetch failure (3 attempts),
 * then a "Couldn't load more — Retry" terminal state instead of an
 * endless spinner.
 */
console.log('[infinite-scroll] script loaded, ts=' + Date.now());

class InfiniteScroll extends HTMLElement {
  connectedCallback() {
    if (this.connected) return; // Defend against duplicate upgrades.
    this.connected = true;
    this.loading = false;
    this.failures = 0;
    this.terminated = false;
    console.log('[infinite-scroll] connectedCallback, dataset.nextUrl=', this.dataset.nextUrl);

    // Primary: IntersectionObserver. Fires on intersection state change,
    // with a generous 600px below-viewport pre-trigger.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.loadNext();
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.observer.observe(this);

    // Fallback: passive scroll + a 2s timer. Both call checkVisible(),
    // which only triggers loadNext when the sentinel is genuinely
    // close to the viewport and we're not already mid-fetch.
    this.checkVisible = this.checkVisible.bind(this);
    window.addEventListener('scroll', this.checkVisible, { passive: true });
    window.addEventListener('resize', this.checkVisible, { passive: true });
    this.pollId = setInterval(this.checkVisible, 2000);
  }

  disconnectedCallback() {
    this.connected = false;
    if (this.observer) this.observer.disconnect();
    if (this.pollId) clearInterval(this.pollId);
    window.removeEventListener('scroll', this.checkVisible);
    window.removeEventListener('resize', this.checkVisible);
  }

  checkVisible() {
    if (this.loading || this.terminated || !this.isConnected) return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh + 600 && rect.bottom > -600) {
      this.loadNext();
    }
  }

  finish() {
    this.terminated = true;
    if (this.observer) this.observer.disconnect();
    this.remove();
  }

  showRetry(message) {
    this.terminated = true;
    if (this.observer) this.observer.disconnect();
    this.classList.add('pa-infinite-scroll--error');
    this.innerHTML = '';
    const label = document.createElement('span');
    label.className = 'pa-infinite-scroll__label';
    label.textContent = message;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pa-infinite-scroll__retry';
    button.textContent = 'Retry';
    button.addEventListener('click', () => {
      this.failures = 0;
      this.terminated = false;
      this.classList.remove('pa-infinite-scroll--error');
      this.innerHTML = '<span class="pa-infinite-scroll__spinner" aria-hidden="true"></span><span class="pa-infinite-scroll__label">Loading more products…</span>';
      this.observer.observe(this);
      this.loadNext();
    });
    this.appendChild(label);
    this.appendChild(button);
    this.removeAttribute('aria-busy');
  }

  async loadNext() {
    if (this.loading || this.terminated) return;
    const url = this.dataset.nextUrl;
    if (!url) {
      console.log('[infinite-scroll] no next URL, finishing');
      this.finish();
      return;
    }
    this.loading = true;
    this.setAttribute('aria-busy', 'true');
    console.log('[infinite-scroll] fetching', url);

    try {
      // 15s timeout via AbortController so a hung fetch doesn't leave
      // the spinner up forever.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const grid = document.getElementById('product-grid');
      const newItems = doc.querySelectorAll('#product-grid > li.grid__item');
      console.log('[infinite-scroll] response parsed:', newItems.length, 'new items');
      if (grid && newItems.length) {
        const frag = document.createDocumentFragment();
        newItems.forEach((li) => frag.appendChild(li));
        grid.appendChild(frag);
      }

      const nextSentinel = doc.querySelector('infinite-scroll');
      const nextUrl = nextSentinel ? nextSentinel.getAttribute('data-next-url') : null;
      console.log('[infinite-scroll] response sentinel:', !!nextSentinel, 'next url:', nextUrl);

      if (!nextUrl || nextUrl === url) {
        // No more pages, or the response loops back to ourselves.
        console.log('[infinite-scroll] last page reached, finishing');
        this.finish();
        return;
      }

      this.dataset.nextUrl = nextUrl;
      this.failures = 0;
      this.loading = false;
      this.removeAttribute('aria-busy');

      // After append the sentinel may still be near the viewport — the
      // periodic check will pick that up on its next tick (≤2s). We
      // intentionally don't recurse synchronously here; that turned
      // out to confuse Shopify's facets re-render in some cases.
    } catch (err) {
      this.failures += 1;
      console.warn('[infinite-scroll] fetch failed (failures=' + this.failures + '):', err);
      this.loading = false;
      this.removeAttribute('aria-busy');
      if (this.failures >= 3) {
        this.showRetry("Couldn't load more products.");
      } else {
        // Exponential backoff: 800ms, 1600ms.
        setTimeout(() => {
          if (!this.terminated) this.loadNext();
        }, 800 * this.failures);
      }
    }
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
